import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldSlash, ArrowClockwise } from "@phosphor-icons/react";
import {
  ALLOWLIST_ENABLED,
  WAREHOUSE_ALLOWLIST,
  isIpAllowed,
  fetchPublicIp,
  hasActiveBypass,
  revokeBypass,
} from "@/lib/warehouseAllowlist";

/**
 * Admin-only card that shows the current network status: visitor IP,
 * whether it matches the warehouse allowlist, and any active off-site
 * bypass. Lets admins revoke their own bypass so they return to
 * warehouse-only access when they leave the office.
 */
export default function NetworkAccessCard() {
  const [ip, setIp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bypass, setBypass] = useState(false);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    setBypass(hasActiveBypass());
    try {
      const v = await fetchPublicIp();
      setIp(v);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const inAllowlist = ip ? isIpAllowed(ip) : false;
  const status = !ALLOWLIST_ENABLED || WAREHOUSE_ALLOWLIST.length === 0
    ? "unconfigured"
    : bypass ? "bypass" : inAllowlist ? "allowed" : "denied";

  const badgeStyles = {
    allowed:      { bg: "bg-emerald-50 dark:bg-emerald-950/40", border: "border-emerald-300 dark:border-emerald-800", text: "text-emerald-800 dark:text-emerald-300", label: "Warehouse network" },
    denied:       { bg: "bg-red-50 dark:bg-red-950/40", border: "border-red-300 dark:border-red-800", text: "text-red-800 dark:text-red-300", label: "Off-site" },
    bypass:       { bg: "bg-amber-50 dark:bg-amber-950/40", border: "border-amber-300 dark:border-amber-800", text: "text-amber-800 dark:text-amber-300", label: "Off-site bypass active" },
    unconfigured: { bg: "bg-zinc-100 dark:bg-zinc-800", border: "border-zinc-300 dark:border-zinc-700", text: "text-zinc-700 dark:text-zinc-300", label: "Gate disabled" },
  }[status];

  return (
    <Card className="p-6 space-y-4" data-testid="network-access-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Network access</div>
          <h2 className="font-heading font-black text-xl mt-1">Warehouse IP gate</h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
            Client-side splash screen that blocks the UI when you're on the wrong network.
            Real security is enforced by the backend allowlist (see docs).
          </p>
        </div>
        <div className={`text-[10px] uppercase tracking-[0.2em] font-black rounded-md px-3 py-1.5 border ${badgeStyles.bg} ${badgeStyles.border} ${badgeStyles.text}`}>
          {status === "allowed" ? <ShieldCheck size={11} weight="fill" className="inline mr-1" /> : <ShieldSlash size={11} weight="fill" className="inline mr-1" />}
          {badgeStyles.label}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Your public IP</div>
          <div className="font-mono text-sm mt-0.5">
            {loading ? "Checking…" : ip || (error ? <span className="text-red-600">{error}</span> : "unknown")}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Allowlist entries</div>
          <div className="font-mono text-xs mt-0.5 text-zinc-700 dark:text-zinc-300">
            {WAREHOUSE_ALLOWLIST.length === 0
              ? <span className="text-amber-600">Empty — gate disabled</span>
              : WAREHOUSE_ALLOWLIST.join(", ")}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={refresh} data-testid="network-refresh">
          <ArrowClockwise size={14} weight="bold" className="mr-1" /> Recheck
        </Button>
        {bypass && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => { revokeBypass(); refresh(); }}
            data-testid="revoke-bypass"
            className="border-amber-400 text-amber-800 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950/30"
          >
            Revoke off-site bypass
          </Button>
        )}
      </div>

      <div className="text-xs text-zinc-500 leading-relaxed">
        Configure the allowlist in <span className="font-mono">frontend/src/lib/warehouseAllowlist.js</span>.
        Off-site admins can add <span className="font-mono">?bypass=&lt;code&gt;</span> to any URL to gain 30-day access.
      </div>
    </Card>
  );
}
